import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Loader2, LocateFixed } from "lucide-react";
import api, { formatPrice, formatApiError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

// Xəritə (Leaflet ~40 KB) yalnız lazım olanda yüklənir.
const PinMap = lazy(() => import("./PinMap"));

const BAKU = { lat: 40.4093, lon: 49.8671 };
// Telefonun mövqeyi yazılan ünvana bu qədər yaxındırsa eyni yer sayılır.
// GPS bina içində 20-100 m yanılır, Wolt-un ünvan tanıması da bir qədər.
const NEAR_M = 300;

const distanceM = (a, b) => {
  const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r;
  const dLon = (b.lon - a.lon) * r;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};
const kmText = (m) => (m / 1000).toFixed(1).replace(".", ",");

/**
 * Çatdırılma ünvanı (təsisçi qərarları, 2026-10-02 və 2026-10-20).
 *
 * KURYER KOORDİNATA GEDİR: Wolt-un sənədinə görə koordinat verilibsə mətn
 * ünvanından ÜSTÜN tutulur. Ona görə koordinat üç yoldan gəlir və hər biri
 * müştəriyə xəritədə GÖRÜNÜR:
 *   1. Wolt yazılan küçəni özü tanıyır → nişan oradadır.
 *   2. Telefonun mövqeyi (icazə ilə) — YALNIZ yazılan ünvana yaxındırsa
 *      avtomatik. Uzaqdırsa SORUŞULUR: işdə oturub ev ünvanını yazan
 *      müştərinin GPS-i kuryeri işə göndərərdi və bunu heç kim görməzdi.
 *   3. Müştəri nişanı xəritədə özü qoyur (icazə verilməyəndə əsas yol).
 *
 * Nişan müştərinin özündəndirsə (2, 3) altında ən yaxın ünvan yazılır
 * («Nişan: … yaxınlığı», serverdən gəlir) — müştəri nişanı düz yerə
 * qoyduğunu görür.
 *
 * İKİ QEYD SAHƏSİ: «mənzil, mərtəbə, giriş, domofon» (haraya) və «kuryerə
 * qeyd» (necə) — kuryerə ikisi birləşib gedir.
 *
 * Mövqe icazəsi ünvan sahəsinə İLK toxunuşda istənir — səhifə açılan kimi
 * soruşmaq refleksli imtina gətirir. Əvvəlcədən verilibsə təkrar soruşulmur.
 *
 * `initialPin` — əvvəlki sifarişdən yadda qalan nişan (cihaz və ya hesab):
 * müştəri onu yenidən qoymur. `onPinChange` nişanı yuxarı ötürür ki,
 * sifarişdən sonra yadda saxlanılsın.
 *
 * `wolt` false olanda (telefonla razılaşma) yalnız mətn sahələri qalır.
 */
export const AddressFields = ({ wolt, addr, setAddr, quote, setQuote, nonce, sms, offline,
                                initialPin, onPinChange, needsAlternative, onAlternative }) => {
  const { t } = useLanguage();
  const [geo, setGeo] = useState("idle");      // idle | asking | granted | denied | unavailable
  const [gps, setGps] = useState(null);
  const [pin, setPin] = useState(initialPin || null); // {lat, lon, source: "gps" | "map"}
  const [woltPos, setWoltPos] = useState(null); // Wolt-un küçəni tanıdığı yer
  const [keepTyped, setKeepTyped] = useState(false); // «yazdığım ünvana» seçildi
  const reqId = useRef(0);
  const baseCache = useRef(null);              // {street, nonce, quote}
  // Ekranda son tanınmış yer qalır ki, küçə yazılarkən xəritə hər hərfdə
  // yox olub-görünməsin. Xəbərdarlıqlar isə YALNIZ təzə `woltPos`-a baxır.
  const lastShown = useRef(null);

  // Yadda qalan nişan sonradan gələ bilər (hesab ünvanları serverdən yüklənir).
  useEffect(() => {
    if (initialPin) setPin(initialPin);
  }, [initialPin]);

  const pinChangeRef = useRef(onPinChange);
  pinChangeRef.current = onPinChange;
  useEffect(() => {
    if (pinChangeRef.current) pinChangeRef.current(pin);
  }, [pin]);

  const locate = useCallback(() => {
    if (!("geolocation" in navigator)) { setGeo("unavailable"); return; }
    setGeo("asking");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setGps({ lat: Number(p.coords.latitude.toFixed(6)), lon: Number(p.coords.longitude.toFixed(6)) });
        setGeo("granted");
      },
      (e) => setGeo(e.code === 1 ? "denied" : "unavailable"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 120000 },
    );
  }, []);

  // İcazə əvvəlcədən verilibsə mövqe səssiz götürülür; rədd edilibsə soruşulmur.
  useEffect(() => {
    if (!wolt) return;
    if (!("geolocation" in navigator)) { setGeo("unavailable"); return; }
    navigator.permissions?.query({ name: "geolocation" })
      .then((st) => {
        if (st.state === "granted") locate();
        else if (st.state === "denied") setGeo("denied");
      })
      .catch(() => {});
  }, [wolt, locate]);

  const onStreet = (value) => {
    setAddr({ ...addr, street: value });
    // Yeni küçə: avtomatik (GPS) nişan yenidən qiymətləndirilir. Müştərinin
    // ÖZ qoyduğu nişan qalır — uzaq düşsə aşağıda xəbərdarlıq çıxır.
    setPin((p) => (p && p.source === "map" ? p : null));
    setKeepTyped(false);
    setWoltPos(null);
  };

  /* Qiymət. Nişan varsa qiymət O NÖQTƏYƏ görə soruşulur, yoxdursa küçəyə görə.
     Küçənin öz tanınması (`woltPos`) hər küçə üçün bir dəfə soruşulur —
     həm nişanın küçədən uzaq olub-olmadığını bilmək, həm də nişanı ilk
     dəfə qoymaq üçün. Son sorğu qalib gəlir (`reqId`). */
  useEffect(() => {
    if (!wolt) return undefined;
    const street = addr.street.trim();
    if (street.length < 5) {
      setQuote({ status: "idle" });
      setWoltPos(null);
      return undefined;
    }
    const id = ++reqId.current;
    let alive = true;
    const current = () => alive && id === reqId.current;
    setQuote({ status: "loading" });
    const timer = setTimeout(async () => {
      const post = (extra) =>
        api.post("/delivery/quote", { street, city: "Bakı", ...extra }).then((r) => r.data);
      const show = (q) => setQuote(q.available
        ? { status: "ready", ...q }
        : { status: "unavailable", kind: q.kind, message: q.message });
      try {
        let base = baseCache.current;
        if (!base || base.street !== street || base.nonce !== nonce) {
          const q = await post({});
          if (!current()) return;
          base = { street, nonce, quote: q };
          baseCache.current = base;
          setWoltPos(q.available && q.lat != null ? { lat: q.lat, lon: q.lon } : null);
          if (!q.available) { show(q); return; }
        }
        if (pin) {
          const q = await post({ lat: pin.lat, lon: pin.lon, source: pin.source });
          if (current()) show(q);
          return;
        }
        const wp = base.quote.lat != null ? { lat: base.quote.lat, lon: base.quote.lon } : null;
        if (gps && wp && !keepTyped && distanceM(gps, wp) <= NEAR_M) {
          setPin({ ...gps, source: "gps" });  // effekt nişanla yenidən işləyir
          return;
        }
        show(base.quote);
      } catch (err) {
        if (current()) setQuote({ status: "error", message: formatApiError(err.response?.data?.detail) });
      }
    }, 900);
    return () => { alive = false; clearTimeout(timer); };
  }, [wolt, addr.street, pin, gps, keepTyped, nonce, setQuote]);

  const streetReady = addr.street.trim().length >= 5;
  const gpsFar = wolt && gps && woltPos && !pin && !keepTyped && distanceM(gps, woltPos) > NEAR_M;
  const pinFar = pin && pin.source === "map" && woltPos && distanceM(pin, woltPos) > NEAR_M;
  const noGeo = geo === "denied" || geo === "unavailable";
  if (woltPos) lastShown.current = woltPos;
  const mapPos = pin
    || (streetReady && (woltPos || lastShown.current))
    || (geo === "granted" && gps)
    || (noGeo ? BAKU : null);
  // Nişan müştərinin özündəndirsə, altında ən yaxın ünvan (serverdən).
  const pinLabel = pin && quote.status === "ready" && quote.pin_label ? quote.pin_label : "";

  const field = "w-full border border-brand-line bg-white px-4 py-3 text-sm outline-none focus:border-brand-ink transition-colors";
  const labelCls = "overline block mb-2";

  return (
    <>
      <div className="grid grid-cols-1 gap-4 mt-5">
        <div>
          <div className="flex items-end justify-between gap-3 mb-2">
            <label className="overline block">{t("address_street")}{wolt ? " *" : ""}</label>
            {wolt && (geo === "idle" || geo === "unavailable") && (
              <button type="button" onClick={locate} data-testid="geo-locate"
                className="flex items-center gap-1 text-xs text-brand-sub hover:text-brand-ink">
                <LocateFixed size={13} strokeWidth={1.5} /> {t("geo_button")}
              </button>
            )}
            {wolt && geo === "asking" && (
              <span className="flex items-center gap-1 text-xs text-brand-muted">
                <Loader2 size={12} className="animate-spin" /> {t("geo_asking")}
              </span>
            )}
          </div>
          <input
            className={field}
            value={addr.street}
            maxLength={200}
            autoComplete="street-address"
            onFocus={() => { if (wolt && geo === "idle") locate(); }}
            onChange={(e) => onStreet(e.target.value)}
            placeholder={t("address_street_ph")}
            data-testid="checkout-street"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{t("address_flat")}</label>
            <input
              className={field}
              value={addr.flat || ""}
              maxLength={120}
              onChange={(e) => setAddr({ ...addr, flat: e.target.value })}
              placeholder={t("address_flat_ph")}
              data-testid="checkout-address-flat"
            />
          </div>
          <div>
            <label className={labelCls}>{t("address_note")}</label>
            <input
              className={field}
              value={addr.note}
              maxLength={300}
              onChange={(e) => setAddr({ ...addr, note: e.target.value })}
              placeholder={t("address_note_ph")}
              data-testid="checkout-address-note"
            />
          </div>
        </div>
      </div>

      {wolt && mapPos && (
        <div className="mt-4">
          <Suspense fallback={<div className="h-56 w-full border border-brand-line bg-brand-bg2" />}>
            <PinMap
              lat={mapPos.lat}
              lon={mapPos.lon}
              zoom={pin || woltPos || gps ? 17 : 12}
              onMove={(lat, lon) => setPin({ lat, lon, source: "map" })}
            />
          </Suspense>

          {gpsFar ? (
            /* Telefon başqa yerdədir — kuryer hara getsin, müştəri özü deyir. */
            <div className="mt-2 border border-brand-line bg-white p-3 text-xs" data-testid="geo-far">
              <p>{t("geo_far").replace("{km}", kmText(distanceM(gps, woltPos)))}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <button type="button" onClick={() => setKeepTyped(true)} data-testid="geo-far-typed"
                  className="border border-brand-ink px-3 py-1.5">{t("geo_far_typed")}</button>
                <button type="button" onClick={() => setPin({ ...gps, source: "gps" })} data-testid="geo-far-here"
                  className="border border-brand-line px-3 py-1.5">{t("geo_far_here")}</button>
              </div>
            </div>
          ) : pinFar ? (
            <div className="mt-2 border border-destructive/40 bg-white p-3 text-xs" data-testid="pin-far">
              <p className="text-destructive">{t("pin_far").replace("{km}", kmText(distanceM(pin, woltPos)))}</p>
              {pinLabel && <p className="mt-1 text-brand-sub">{t("pin_near").replace("{place}", pinLabel)}</p>}
              <button type="button" onClick={() => { setPin(null); setKeepTyped(true); }}
                data-testid="pin-far-reset" className="mt-2 border border-brand-line px-3 py-1.5">
                {t("pin_far_reset")}
              </button>
            </div>
          ) : (
            <p className="text-xs text-brand-muted mt-2" data-testid="map-hint">
              {pin?.source === "gps" ? `✓ ${t("geo_used")}`
                : pin?.source === "map" ? `✓ ${t("map_pin_set")}`
                : noGeo ? t("geo_denied")
                : t("map_hint")}
              {pinLabel && (
                <span className="block mt-0.5 text-brand-sub" data-testid="pin-label">
                  {t("pin_near").replace("{place}", pinLabel)}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {wolt && quote.status !== "idle" && (
        <div className="mt-4 text-sm" data-testid="quote-box" aria-live="polite">
          {quote.status === "loading" && (
            <p className="flex items-center gap-2 text-brand-muted">
              <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
              {t("quote_loading")}
            </p>
          )}
          {quote.status === "ready" && (
            <div className="border border-brand-line bg-white p-4 space-y-1.5">
              <p className="flex justify-between">
                <span>{t("delivery_fee_label")}</span>
                <span className="font-medium" data-testid="quote-fee">{formatPrice(quote.fee)}</span>
              </p>
              {/* Wolt ünvanı özü tanıyır və «həmişə nəsə tapır» — səhv
                  tanınmış ünvanı yalnız müştəri görə bilər. */}
              <p className="text-xs text-brand-muted" data-testid="quote-address">
                {t("quote_recognized")}:{" "}
                <span className="text-brand-ink">{quote.formatted_address}</span>.{" "}
                {t("quote_check_hint")}
              </p>
              {/* Mağaza «bağlı» edibsə vəd verilmir — sahibkar zəng edib vaxtı
                  deyəcək (təsisçi mətni, 2026-10-20). */}
              <p className="text-xs text-brand-muted" data-testid="quote-note">
                {offline ? t("delivery_offline_note")
                  : quote.scheduled ? t("quote_scheduled_note") : t("quote_dispatch_note")}
                {sms && !offline && ` ${t("quote_sms_note")}`}
              </p>
            </div>
          )}
          {(quote.status === "unavailable" || quote.status === "error") && (
            <div className="border border-destructive/40 bg-white p-4" data-testid="quote-unavailable">
              <p className="text-destructive">{quote.message}</p>
              {needsAlternative && (
                <>
                  <p className="text-xs text-brand-muted mt-1.5">{t("quote_alternatives")}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button type="button" onClick={() => onAlternative("pickup")} data-testid="alt-pickup"
                      className="border border-brand-line px-3 py-2 text-xs hover:border-brand-ink">
                      {t("delivery_pickup")}
                    </button>
                    <button type="button" onClick={() => onAlternative("manual")} data-testid="alt-manual"
                      className="border border-brand-line px-3 py-2 text-xs hover:border-brand-ink">
                      {t("delivery_manual")}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
